/* tslint:disable:no-trailing-whitespace align typedef-whitespace */

import { Window                } from './window';
import { DockManager           } from 'dock-spawn-ts/lib/js/DockManager';
import { Logger                } from '@/util/logger';
import { DocumentMetadata      } from './document.metadata';
import { DocumentTab, DocumentWindowManager } from './document.window.manager';

/**
 *
 * @class DocumentWindow
 *
 * @brief base class for view panels that get "tabbed" in the main document viewing window (the big
 * center part of the GUI).  DocumentWindow has a DocumentMetadata, which encapsulates the definition/state
 * of the document.  A DocumentMeta always has enough information to save/restore a document window.  So
 * new kinds (sub-classes) of DocumentWindow can just create an provide their own DocumentMeta specializations
 * as needed.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class DocumentWindow extends Window {

  protected       meta:     DocumentMetadata = null;

  private   tabManager: DocumentWindowManager = null;

  /** standard constructor */
  constructor(
    tabManager: DocumentWindowManager,
          meta: DocumentMetadata,
       manager: DockManager,
  ) {

    super(
      meta.id,
      manager,
      meta.type,
      meta.title,
      meta.icon,
      false
    );

    let dw: DocumentWindow = this;

    dw.tabManager = tabManager;

    Logger.debug('[DocumentWindow] constructing...');

    dw.meta = meta;

    setTimeout(() => {

      dw.redraw();

    }, 100);
  }

  public get title(): string {

    return super.title;
  }

  /** set the window title */
  public set title(title: string) {

    /* before redrawing, double-check the title for uniqueness */

    if (!this.tabManager) {

      super.title = title;

      return ;
    }

    let tabs: DocumentTab[] = this.tabManager.currentTabs().filter(x => x.window.id() != this.id());
    let similar: Window[]   = tabs.filter(x => x.window.title == title).map(x => x.window);

    if ((similar.length == 0) || (title == '')) {

      /* no need to make unique */

      super.title = title;

      return ;
    }

    /* we have to add a prefix to our title, since at least one other tab already has this title */

    let n: number        = 1;
    let newTitle: string = title;

    while (true) {

      /* try the next prefix ... */

      newTitle = `(${n}) ${title}`;
      similar  = tabs.filter(x => x.window.title == newTitle).map(x => x.window);

      if (similar.length == 0) {

        /* no other tab with this prefix, we can use it */

        break;
      }

      n++;

      if (n > 50) {

        /* should never get this high */

        break;
      }
    }

    super.title = newTitle;

    Logger.debug('[DocumentWindow] set title - made title unique: ', super.title);
  }

  public redraw(): void {

    /* do the usual redraw */

    super.redraw();
  }

  /** after initialization, some windows may allow for resetting tot new content to view */
  public refocus(meta: DocumentMetadata, append: boolean=false) {

    Logger.debug(`[DocumentWindow] '${this.title}' focusing (${append}) on: `, meta);

  }


  /** windows than can act as an anchor (default) window for their group/kind can "drop anchor" */
  public dropAnchor() {

  }

  /** opposite of dropping anchor, group/kind of windows will not have an anchor unitl another one is dropped. */
  public raiseAnchor() {
  }

  /** close the project view (if its open) */
  public close(implied: boolean = false) {

    super.close(implied);

    /* we use implied to indicate that the dock spawn manager is doing the closing; not us. */

    this.tabManager.closeTab(this.meta.id, implied);
  }

}
